// @ts-nocheck
// features/chat/useChatAutoScroll.ts
import { useRef, useCallback, useEffect, useReducer } from 'react';
import { FlatList } from 'react-native';

interface UseChatAutoScrollOptions {
  messageCount: number;
  isLoading: boolean;
  isSearching: boolean;
  shouldAutoScroll: boolean;
  extraDeps?: readonly any[];
}

export interface UseChatAutoScrollReturn {
  flatListRef: React.RefObject<FlatList<any>>;
  onScroll: (event: any) => void;
  onContentSizeChange: (w: number, h: number) => void;
  showScrollToBottom: boolean;
  forceScrollToBottom: () => void;
  scrollToBottom: (animated?: boolean) => void;
}

const AT_BOTTOM_THRESHOLD = 40;

export function useChatAutoScroll({
  messageCount,
  isLoading,
  isSearching,
  shouldAutoScroll,
  extraDeps = [],
}: UseChatAutoScrollOptions): UseChatAutoScrollReturn {
  const flatListRef = useRef<FlatList<any>>(null);
  const isUserScrolledUpRef = useRef(false);
  const lastContentHeightRef = useRef(0);
  const forceScrollRef = useRef(true);
  const contentSizeScrolledRef = useRef(false);

  const [showScrollToBottom, toggleFAB] = useReducer(
    (_: boolean, next: boolean) => next,
    false
  );

  const scrollToBottom = useCallback((animated = true) => {
    try {
      flatListRef.current?.scrollToEnd({ animated });
    } catch {}
  }, []);

  const forceScrollToBottom = useCallback(() => {
    forceScrollRef.current = true;
    contentSizeScrolledRef.current = false;
    setTimeout(() => {
      scrollToBottom(false);
      forceScrollRef.current = false;
    }, 0);
  }, [scrollToBottom]);

  useEffect(() => {
    if (contentSizeScrolledRef.current) {
      contentSizeScrolledRef.current = false;
      return;
    }

    // Forced scroll — conversation switch / mount
    if (forceScrollRef.current && !isUserScrolledUpRef.current) {
      const t = setTimeout(() => {
        scrollToBottom(false);
        forceScrollRef.current = false;
      }, 0);
      return () => clearTimeout(t);
    }

    // ✅ FIX: גלול תמיד כשיש תוכן חדש והמשתמש לא גלל למעלה ידנית.
    // לא תלוי ב-shouldAutoScroll — פותר את הבעיה שהצ'אט לא גולל
    // להודעות חדשות לפני שהמשתמש שלח משהו.
    if (!isUserScrolledUpRef.current) {
      const t = setTimeout(() => scrollToBottom(true), 60);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageCount, isLoading, isSearching, shouldAutoScroll, scrollToBottom, ...extraDeps]);

  const onScroll = useCallback(
    (event: any) => {
      const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
      const distanceFromBottom =
        contentSize.height - (layoutMeasurement.height + contentOffset.y);
      const atBottom = distanceFromBottom <= AT_BOTTOM_THRESHOLD;

      isUserScrolledUpRef.current = !atBottom;

      if (atBottom && showScrollToBottom) toggleFAB(false);
      else if (!atBottom && !showScrollToBottom) toggleFAB(true);
    },
    [showScrollToBottom]
  );

  const onContentSizeChange = useCallback(
    (_w: number, h: number) => {
      const grew = h > lastContentHeightRef.current;
      lastContentHeightRef.current = h;

      if (!grew) return;

      if (forceScrollRef.current && !isUserScrolledUpRef.current) {
        scrollToBottom(false);
        forceScrollRef.current = false;
        contentSizeScrolledRef.current = true;
        return;
      }

      // ✅ FIX: גלול אם המשתמש לא גלל למעלה, ללא תלות ב-shouldAutoScroll
      if (!isUserScrolledUpRef.current) {
        scrollToBottom(false);
        contentSizeScrolledRef.current = true;
      }
    },
    [scrollToBottom]
  );

  return {
    flatListRef,
    onScroll,
    onContentSizeChange,
    showScrollToBottom,
    forceScrollToBottom,
    scrollToBottom,
  };
}